import { LEGAL_PUBLISHED } from '@lib/constants/legal'
import { safeRedirectPath } from '@lib/helpers/safe-redirect'
import * as React from 'react'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { useAuth } from '@/auth/AuthContext'
import { isApiError } from '@/helpers/apiClient'
import { toErrorMessage } from '@/helpers/logger'
import { z } from 'zod'
import { loginInputSchema } from '@/schemas/auth.schema'
import { authService } from '@/services/AuthService'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Field } from '@/components/ui/field'
import { FormError } from '@/components/ui/form-error'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { AuthShell } from '@/components/AuthShell'

/**
 * The page a visitor was refused, once it has been checked as an in-app path.
 *
 * Router state is whatever the previous page put there. A string is not enough:
 * `//evil.example` and `https://evil.example` both navigate off this origin, and
 * a magic-link request would store the same value. `safeRedirectPath` is the
 * check the API already makes before it honors a destination.
 */
const redirectStateSchema = z.object({ from: z.string() }).partial()

function redirectFrom<S>(state: S): string | null {
  const parsed = redirectStateSchema.safeParse(state)
  if (!parsed.success) return null
  return safeRedirectPath(parsed.data.from)
}

/** Where a successful password sign-in goes. Home when the stashed path is not one of ours. */
function resolveRedirect<S>(state: S): string {
  return redirectFrom(state) ?? '/'
}

/**
 * The page the visitor was refused, or null if they simply came to sign in.
 *
 * Distinct from `resolveRedirect`, which falls back to `/` so that a successful
 * password sign-in always has somewhere to go. A sign-in link needs to know the
 * difference: sending `/` as the destination would write one onto every token
 * ever issued, when almost none of them are finishing an interrupted journey.
 */
function attemptedPath<S>(state: S): string | null {
  return redirectFrom(state)
}

/** Vendor signup, carrying on afterward to the page the visitor was refused, if any. */
function vendorSignupPath(attempted: string | null): string {
  return attempted === null ? '/signup/vendor' : `/signup/vendor?next=${encodeURIComponent(attempted)}`
}

/** Turn an API/network failure into a message suitable for the sign-in form. */
function loginErrorMessage<E>(error: E): string {
  if (isApiError(error)) {
    if (error.status === 401) return 'Incorrect email or password.'
    return error.body.message ?? 'Unable to sign in. Please try again.'
  }
  return toErrorMessage(error)
}

/** Email + password sign-in page. Redirects to the attempted route on success. */
export function LoginPage() {
  const { login } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const [email, setEmail] = React.useState<string>('')
  const [password, setPassword] = React.useState<string>('')
  const [fieldError, setFieldError] = React.useState<string | null>(null)
  const [formError, setFormError] = React.useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false)
  const [linkSent, setLinkSent] = React.useState<boolean>(false)
  const [isSendingLink, setIsSendingLink] = React.useState<boolean>(false)

  /**
   * Email a sign-in link instead of asking for a password.
   *
   * The destination goes with the request: whoever arrived here was probably
   * sent by `ProtectedRoute`, which stashes the page it refused them, and that
   * intent is worth carrying through the email rather than dropping them on a
   * home page they did not ask for.
   *
   * The confirmation is the same whatever happens, including when the address
   * belongs to nobody — the API will not say, and a screen that did would undo
   * the point of it not saying.
   */
  async function sendMagicLink(): Promise<void> {
    setFieldError(null)
    setFormError(null)

    const parsed = z.string().trim().toLowerCase().email().safeParse(email)
    if (!parsed.success) {
      setFieldError('Enter your email address and we will send you a link')
      return
    }

    setIsSendingLink(true)
    try {
      const from = attemptedPath(location.state)
      await authService.requestMagicLink({ email: parsed.data, ...(from === null ? {} : { redirectTo: from }) })
    } catch {
      // Swallowed on purpose. A failure here is either the throttler or the
      // network, and reporting it differently from success would tell whoever
      // is asking something about the address. It is already logged.
    } finally {
      setIsSendingLink(false)
      setLinkSent(true)
    }
  }

  async function handleSubmit(
    event: React.FormEvent<HTMLFormElement>,
  ): Promise<void> {
    event.preventDefault()
    setFieldError(null)
    setFormError(null)

    const parsed = loginInputSchema.safeParse({ email, password })
    if (!parsed.success) {
      setFieldError(parsed.error.issues[0]?.message ?? 'Invalid credentials')
      return
    }

    setIsSubmitting(true)
    try {
      await login(parsed.data)
      navigate(resolveRedirect(location.state), { replace: true })
    } catch (error) {
      setFormError(loginErrorMessage(error))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <AuthShell>
      <Card size="lg" className="w-full max-w-sm">
        <CardHeader>
          <CardTitle className="text-display">Sign in</CardTitle>
          <CardDescription>
            Enter your credentials to sign in.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={handleSubmit} noValidate>
            <Field label="Email" htmlFor="email">
              <Input
                id="email"
                type="email"
                // `username`, not `email`: it is what password managers look
                // for to pair a saved password with its account.
                autoComplete="username"
                placeholder="you@example.com"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </Field>
            <Field label="Password" htmlFor="password">
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
            </Field>

            {fieldError ? (
              <FormError>{fieldError}</FormError>
            ) : null}
            {formError ? (
              <FormError>{formError}</FormError>
            ) : null}

            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : null}
              {isSubmitting ? 'Signing in…' : 'Sign in'}
            </Button>
            {linkSent ? (
              <p className="border border-line bg-surface-sunken p-3 text-sm text-ink-muted">
                If that address has an account, a sign-in link is on its way. It lasts 15 minutes.
              </p>
            ) : (
              <Button
                type="button"
                variant="outline"
                className="w-full"
                disabled={isSubmitting}
                onClick={() => void sendMagicLink()}
                pending={isSendingLink}
              >
                {isSendingLink ? 'Sending…' : 'Email me a sign-in link'}
              </Button>
            )}
            <div className="flex flex-col items-center gap-2">
              {/* Offered only once the terms and privacy policy are published —
                  signup is closed until then. See LEGAL_PUBLISHED. */}
              {LEGAL_PUBLISHED ? (
                <>
                  <Link to="/signup" className="text-sm text-ink-muted underline">
                    New here? Start a free trial
                  </Link>
                  {/* Carries on to where they were going: "Ask to serve this venue" on a venue's page sends them here. */}
                  <Link to={vendorSignupPath(attemptedPath(location.state))} className="text-sm text-ink-muted underline">
                    A vendor? Join free
                  </Link>
                </>
              ) : null}
              <Link to="/forgot-password" className="text-sm text-ink-muted underline">
                Forgot password?
              </Link>
              <p className="text-sm text-ink-muted">
                <Link to="/terms" className="underline">
                  Terms of service
                </Link>
                {' · '}
                <Link to="/privacy" className="underline">
                  Privacy policy
                </Link>
              </p>
            </div>
          </form>
        </CardContent>
      </Card>
    </AuthShell>
  )
}
